import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import { Typography, Box, Paper } from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Home as HomeIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Cancel as CancelIcon,
  GroupAdd as GroupAddIcon,
  Business as BusinessIcon,
  Lock as LockIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { AdminUserActivity } from '../../../types/admin';
import { formatDistanceToNow, format } from 'date-fns';

interface UserActivityTimelineProps {
  activities: AdminUserActivity[];
  maxItems?: number;
}

/**
 * UserActivityTimeline - Activity timeline for user detail view
 *
 * Features:
 * - Material-UI Timeline component
 * - Activity type icons
 * - Relative timestamps (e.g., "2 hours ago")
 * - Hover for absolute timestamp
 * - Reverse chronological order (latest first)
 * - Color-coded by activity type
 * - Metadata display when available
 */
export default function UserActivityTimeline({
  activities,
  maxItems = 50,
}: UserActivityTimelineProps) {
  const displayActivities = activities.slice(0, maxItems);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'user_signup':
        return <PersonAddIcon />;
      case 'home_created':
        return <HomeIcon />;
      case 'home_updated':
        return <EditIcon />;
      case 'home_archived':
        return <ArchiveIcon />;
      case 'subscription_upgraded':
        return <TrendingUpIcon />;
      case 'subscription_downgraded':
        return <TrendingDownIcon />;
      case 'subscription_cancelled':
        return <CancelIcon />;
      case 'invited_agent':
        return <GroupAddIcon />;
      case 'joined_business':
      case 'left_business':
        return <BusinessIcon />;
      case 'profile_updated':
        return <EditIcon />;
      case 'password_changed':
        return <LockIcon />;
      case 'login':
        return <LoginIcon />;
      case 'logout':
        return <LogoutIcon />;
      case 'task_created':
        return <AssignmentIcon />;
      case 'task_completed':
        return <CheckCircleIcon />;
      case 'image_uploaded':
        return <ImageIcon />;
      case 'document_uploaded':
        return <DescriptionIcon />;
      default:
        return <EditIcon />;
    }
  };

  const getActivityColor = (type: string): 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'grey' => {
    switch (type) {
      case 'user_signup':
        return 'success';
      case 'home_created':
      case 'task_created':
        return 'primary';
      case 'subscription_upgraded':
        return 'success';
      case 'subscription_downgraded':
      case 'subscription_cancelled':
        return 'warning';
      case 'home_archived':
      case 'left_business':
        return 'grey';
      case 'task_completed':
        return 'success';
      case 'login':
      case 'joined_business':
        return 'info';
      case 'logout':
        return 'grey';
      default:
        return 'primary';
    }
  };

  if (activities.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No activity recorded yet
        </Typography>
      </Box>
    );
  }

  return (
    <Timeline position="right">
      {displayActivities.map((activity, index) => (
        <TimelineItem key={activity.id}>
          <TimelineOppositeContent
            sx={{ m: 'auto 0', flex: 0.2, pr: 2 }}
            align="right"
            variant="body2"
            color="text.secondary"
          >
            <Box
              component="span"
              title={format(new Date(activity.timestamp), 'PPpp')}
              sx={{ cursor: 'help' }}
            >
              {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
            </Box>
          </TimelineOppositeContent>

          <TimelineSeparator>
            <TimelineDot color={getActivityColor(activity.type)} variant="outlined">
              {getActivityIcon(activity.type)}
            </TimelineDot>
            {index < displayActivities.length - 1 && <TimelineConnector />}
          </TimelineSeparator>

          <TimelineContent sx={{ py: '12px', px: 2 }}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="body2" component="span">
                {activity.description}
              </Typography>
              {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {activity.metadata.homeName && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Home: {activity.metadata.homeName}
                    </Typography>
                  )}
                  {activity.metadata.tier && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Tier: {activity.metadata.tier}
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          </TimelineContent>
        </TimelineItem>
      ))}

      {activities.length > maxItems && (
        <TimelineItem>
          <TimelineOppositeContent sx={{ flex: 0.2 }} />
          <TimelineSeparator>
            <TimelineDot variant="outlined" />
          </TimelineSeparator>
          <TimelineContent>
            <Typography variant="body2" color="text.secondary">
              {activities.length - maxItems} more activities...
            </Typography>
          </TimelineContent>
        </TimelineItem>
      )}
    </Timeline>
  );
}
